<template>
  <div id="app" class="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 flex flex-col">
    <nav v-if="!isAuthPage" class="bg-gradient-to-r from-amber-900/50 via-black/95 to-gray-900/90 backdrop-blur-sm shadow-xl border-b border-amber-600/30 sticky top-0 z-50">
      <div class="container mx-auto px-4 py-3">
        <div class="flex justify-between items-center">
          <router-link to="/home" class="flex items-center space-x-3 group">
            <div class="w-12 h-12 relative overflow-hidden rounded-full bg-black/60 flex items-center justify-center p-1 transition-all duration-300 group-hover:shadow-amber-500/50 group-hover:shadow-xl border border-amber-600/40">
              <svg class="w-9 h-9 group-hover:scale-110 transition-all duration-300" viewBox="0 0 24 24">
                <!-- Cercle extérieur -->
                <circle cx="12" cy="12" r="10.5" fill="none" stroke="#FFC107" stroke-width="1" />
                
                <!-- Marteau de guerre Stormcast authentique -->
                <g>
                  <!-- Manche du marteau -->
                  <rect x="11.5" y="8" width="1" height="11" fill="#D4AF37" />
                  
                  <!-- Garde décorée -->
                  <rect x="9.5" y="17.5" width="5" height="1" fill="#D4AF37" />
                  <path d="M10.5,18.5 l1.5,1 l1.5,-1 l-1.5,-0.5 z" fill="#D4AF37" />
                  
                  <!-- Tête du marteau - style Sigmarite -->
                  <path d="M7,5 h10 v3 c0,0 -2,1 -5,1 c-3,0 -5,-1 -5,-1 z" fill="#D4AF37" />
                  <rect x="7" y="5" width="10" height="1.5" fill="#D4AF37" />
                  
                  <!-- Runes et gravures -->
                  <path d="M8.5,6 h1 v1 h-1 z" fill="#111827" />
                  <path d="M11.5,6 h1 v1 h-1 z" fill="#111827" />
                  <path d="M14.5,6 h1 v1 h-1 z" fill="#111827" />
                  
                  <!-- Détails de la tête de marteau -->
                  <path d="M7,5 l-1.5,-1 v3 l1.5,0 z" fill="#D4AF37" />
                  <path d="M17,5 l1.5,-1 v3 l-1.5,0 z" fill="#D4AF37" />
                  
                  <!-- Aura lumineuse -->
                  <path d="M12,3.5 l0.5,1 l-1,0 z" fill="#D4AF37" opacity="0.7" />
                  <path d="M12,3.5 l-1,1 l2,0 z" fill="#D4AF37" opacity="0.7" />
                  
                  <!-- Éclairs stylisés -->
                  <path d="M9,7.5 l-0.5,1 l0.8,0 l-0.3,1 l1,-1.2 l-0.7,0 l0.2,-0.8 z" fill="#FFD700" />
                  <path d="M15,7.5 l-0.5,1 l0.8,0 l-0.3,1 l1,-1.2 l-0.7,0 l0.2,-0.8 z" fill="#FFD700" />
                </g>
              </svg>
            </div>
            <div class="transition-all duration-300 group-hover:translate-x-1">
              <span class="text-amber-400 font-bold text-2xl">SPEARHEAD</span>
              <span class="block text-amber-300/80 text-sm font-medium">League Manager</span>
            </div>
          </router-link>
          
          <div class="hidden md:flex items-center space-x-6">
            <router-link 
              to="/home" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2 relative group"
              active-class="text-amber-400"
            >
              <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 transition-all duration-300 group-hover:w-full"></span>
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              <span>Accueil</span>
            </router-link>
            <router-link 
              to="/matches" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
              active-class="text-amber-400"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              <span>Matchs</span>
            </router-link>
            
            <router-link 
              to="/rankings" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
              active-class="text-amber-400"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
              </svg>
              <span>Classements</span>
            </router-link>
            
            <router-link 
              to="/statistics" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
              active-class="text-amber-400"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
              </svg>
              <span>Statistiques</span>
            </router-link>
            
            <router-link 
              to="/messages" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2 relative"
              active-class="text-amber-400"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
              </svg>
              <span>Messages</span>
              <!-- Indicateur de messages non lus -->
              <span v-if="unreadCount > 0" class="ml-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                {{ unreadCount > 9 ? '9+' : unreadCount }}
              </span>
            </router-link>
            
            <router-link v-if="currentUser"
              to="/profile" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
              active-class="text-amber-400"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
              <span>Profil</span>
            </router-link>
            
            <template v-if="isAdmin">
              <router-link 
                to="/admin" 
                class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
                active-class="text-amber-400"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                <span>Administration</span>
              </router-link>
            </template>
            
            <button v-if="currentUser"
              @click="logOut" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
              </svg>
              <span>Déconnexion</span>
            </button>
          </div>
          
          <div v-if="!currentUser" class="flex items-center space-x-4 mt-2">
            <router-link 
              to="/login" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"/>
              </svg>
              <span>Connexion</span>
            </router-link>
            
            <router-link 
              to="/register" 
              class="text-yellow-300 hover:text-amber-400 transition-colors duration-200 flex items-center space-x-2"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
              </svg>
              <span>Inscription</span>
            </router-link>
          </div>
          
          <div class="md:hidden absolute top-4 right-4">
            <button @click="mobileMenuOpen = !mobileMenuOpen" class="text-amber-400 hover:text-amber-300 focus:outline-none">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path v-if="!mobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <div v-if="mobileMenuOpen" class="absolute top-full left-0 right-0 bg-black/95 border-b border-amber-600/40 md:hidden shadow-lg">
            <div class="px-2 pt-2 pb-3 space-y-1">
              <router-link to="/home" class="block px-3 py-2 text-amber-400 font-medium rounded-md hover:bg-amber-900/30">Accueil</router-link>
              <router-link to="/matches" class="block px-3 py-2 text-amber-300/90 font-medium rounded-md hover:bg-amber-900/30">Matchs</router-link>
              <router-link to="/rankings" class="block px-3 py-2 text-amber-300/90 font-medium rounded-md hover:bg-amber-900/30">Classements</router-link>
              <router-link to="/statistics" class="block px-3 py-2 text-amber-300/90 font-medium rounded-md hover:bg-amber-900/30">Statistiques</router-link>
              <router-link to="/messages" class="block px-3 py-2 text-amber-300/90 font-medium rounded-md hover:bg-amber-900/30">Messages</router-link>
              <router-link v-if="currentUser" to="/profile" class="block px-3 py-2 text-amber-300/90 font-medium rounded-md hover:bg-amber-900/30">Profil</router-link>
              <router-link v-if="isAdmin" to="/admin" class="block px-3 py-2 text-amber-300/90 font-medium rounded-md hover:bg-amber-900/30">Administration</router-link>
              <button v-if="currentUser" @click="logOut" class="w-full text-left block px-3 py-2 text-amber-300/90 font-medium rounded-md hover:bg-amber-900/30">Déconnexion</button>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <main class="flex-grow">
      <router-view />
    </main>

    <footer v-if="!isAuthPage" class="bg-black/90 backdrop-blur-sm border-t border-amber-600/30">
      <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <p class="text-amber-300/80 text-sm">
            © {{ new Date().getFullYear() }} SPEARHEAD Ladder Manager. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import MessageService from '@/services/message.service';

export default {
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    isAdmin() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes('ROLE_ADMIN');
      }
      return false;
    },
    isAuthPage() {
      return ['/login', '/register'].includes(this.$route.path);
    }
  },
  data() {
    return {
      mobileMenuOpen: false,
      unreadCount: 0
    }
  },
  methods: {
    logOut() {
      this.$store.dispatch('auth/logout');
      this.$router.push('/login');
    },
    async loadUnreadCount() {
      if (this.currentUser) {
        try {
          const response = await MessageService.getUnreadCount();
          this.unreadCount = response.data;
        } catch (error) {
          console.error('Erreur lors du chargement des messages non lus:', error);
        }
      }
    }
  },
  watch: {
    currentUser: {
      handler(newUser) {
        if (newUser) {
          this.loadUnreadCount();
        } else {
          this.unreadCount = 0;
        }
      },
      immediate: true
    },
    $route(to) {
      const pageTitles = {
        '/': 'SPEARHEAD League Manager - Plateforme de classement de joueurs',
        '/home': 'SPEARHEAD League Manager - Accueil',
        '/profile': 'SPEARHEAD League Manager - Mon profil',
        '/admin': 'SPEARHEAD League Manager - Administration',
        '/messages': 'SPEARHEAD League Manager - Messages',
        '/login': 'SPEARHEAD League Manager - Connexion',
        '/register': 'SPEARHEAD League Manager - Inscription'
      };
      document.title = pageTitles[to.path] || 'SPEARHEAD League Manager';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = 'SPEARHEAD League Manager - Plateforme de classement pour les joueurs de SPEARHEAD.';
        document.head.appendChild(meta);
      }
    }
  },
  mounted() {
    const metaTags = [
      {
        name: 'keywords',
        content: 'SPEARHEAD, classement, matchs, joueurs, statistiques, gaming'
      },
      {
        property: 'og:title',
        content: 'SPEARHEAD League Manager - Plateforme de classement'
      },
      {
        property: 'og:description',
        content: 'Gérez vos matchs, consultez les classements et suivez vos statistiques.'
      },
      {
        property: 'og:type',
        content: 'website'
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image'
      }
    ];

    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      Object.keys(tag).forEach(key => {
        meta[key] = tag[key];
      });
      document.head.appendChild(meta);
    });

    // Écouter l'événement de messages lus pour mettre à jour le compteur
    window.addEventListener('messagesRead', () => {
      console.log('Événement messagesRead reçu, rechargement du compteur...');
      this.loadUnreadCount();
    });
  },
  beforeUnmount() {
    // Nettoyer l'écouteur d'événement
    window.removeEventListener('messagesRead', this.loadUnreadCount);
  }
};
</script>

<style>
body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  background-color: #000000;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-image: 
    radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.03) 1px, transparent 1px),
    radial-gradient(circle at 75% 75%, rgba(212, 175, 55, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

main {
  flex: 1;
}

:root {
  --primary-color: #D4AF37;
  --primary-hover: #FFD700;
  --dark-bg: #000000;
  --dark-bg-alt: #111111;
  --text-color: #D4AF37;
  --text-muted: #8B7D39;
}

/* Personnalisation des scrollbars */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #111111;
}

::-webkit-scrollbar-thumb {
  background: #D4AF37;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #FFD700;
}
</style> 